import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ChurchUnitsDataService, CampusStake, CampusStakeDetails, ConfirmDialog, MessageDialog, IComponentHasChanges, CampusStakeDetailsForUpdate } from '../../shared';
import { MatDialog } from '@angular/material/dialog';
import * as Config from '../../shared/tokens/constants.tokens';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-campus-stakes',
  templateUrl: './campus-stakes.component.html',
  styleUrls: ['./campus-stakes.component.scss']
})
export class CampusStakesComponent implements OnInit, IComponentHasChanges {
  loading = false;
  loadingError = false;
  campusStakes: CampusStake[];

  selectedCampusStake: CampusStakeDetailsForUpdate;
  loadingCampusStakeDetails = false;

  campusStakeForm: FormGroup;
  campusStake: CampusStakeDetails;
  actionTitle: string;

  deleting = false;
  saving = false;

  saved = false;
  deleted = false;

  constructor(private churchUnitsDataService: ChurchUnitsDataService, private formBuilder: FormBuilder, private matDialog: MatDialog) {}

  ngOnInit() {
    this.campusStakeForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(80)]],
      unitNumber: [Config.Constants.unassignedUnitNumber, [Validators.required, Validators.pattern(Config.Constants.unitNumberRegex)]],
      email: ['', [Validators.maxLength(70), Validators.pattern(Config.Constants.emailRegex)]],
      phone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      password: ['', Validators.required]
    });
    this.loadData();
  }

  /**
   * Indicates if we have unsaved changes to the stake's information.
   */
  hasUnsavedChanges(): boolean {
    return this.campusStakeForm.dirty;
  }

  /**
   * Gets the message to give the user when we have unsaved changes and they're trying to navigate away.
   */
  get unsavedMessage(): string {
    return 'You have unsaved changes to the stake\'s information.  Are you sure you want to continue?';
  }

  /**
   * Indicates that the stake's unit number is unassigned; really just assigns it to our placeholder value.
   */
  setUnassignedUnitNumber() {
    this.campusStakeForm.get('unitNumber').setValue(Config.Constants.unassignedUnitNumber);
  }

  /**
   * Indicates that the stake's unit number should be entered; really just clears the placeholder value that was there.
   */
  clearUnassignedUnitNumber() {
    if (this.campusStake && this.campusStake.unitNumber !== Config.Constants.unassignedUnitNumber) {
      this.campusStakeForm.get('unitNumber').setValue(this.campusStake.unitNumber);
    } else {
      this.campusStakeForm.get('unitNumber').setValue('');
    }
  }

  /**
   * Indicates whether or not our form group the stake to have a unit number.
   */
  unitNumberAssigned() {
    return this.campusStakeForm.get('unitNumber').value !== Config.Constants.unassignedUnitNumber;
  }

  /**
   * Tells you if the given form control has the given validation error.
   */
  hasError(formControl: FormControl, error: string): boolean {
    return (formControl.touched || formControl.dirty) && formControl.hasError(error);
  }

  /**
   * Whether or not we're editing or adding.
   */
  isEditing() {
    return this.campusStake.campusStakeId > 0;
  }

  /**
   * Load the selected stake's details so the user can modify them.
   */
  loadSelectedStakeDetails() {
    if (!this.loadingCampusStakeDetails && this.selectedCampusStake) {
      this.turningOffFlags();
      this.loadingCampusStakeDetails = true;
      this.churchUnitsDataService
        .getCampusStakeDetailsForUpdate(this.selectedCampusStake.campusStakeId)
        .pipe(
          finalize(() => (this.loadingCampusStakeDetails = false))
        )
        .subscribe(stake => {
          this.setStakeDetails(stake);
        });
    }
  }

  turningOffFlags() {
    this.saved = false;
    this.deleted = false;
  }

  /**
   * Gets our component ready for the user to add a new campus stake.
   */
  createCampusStake() {
    this.clearSelectedStake();
    this.turningOffFlags();
    const stake = new CampusStakeDetailsForUpdate();
    stake.campusStakeId = 0;
    this.setStakeDetails(stake);
  }

  /**
   * Save's the stake information, either creating a new one or updating it.
   */
  save() {
    if (this.saving || !this.campusStakeForm.dirty || !this.campusStakeForm.valid) {
      return;
    }

    const stake = Object.assign({}, this.campusStake, this.campusStakeForm.value);

    this.turningOffFlags();

    this.saving = true;
    if (this.isEditing()) {
      this.churchUnitsDataService
        .updateCampusStake(stake)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          updatedStake => {
            this.setStakeDetails(updatedStake);

            // update stake's name in dropdown if changed
            const index = this.campusStakes.findIndex(s => s.campusStakeId === updatedStake.campusStakeId);
            this.campusStakes.splice(index, 1);
            this.campusStakes.push(updatedStake);

            // select updated stake
            this.selectedCampusStake = updatedStake;
            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    } else {
      this.churchUnitsDataService
        .createCampusStake(stake)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          createdStake => {
            this.setStakeDetails(createdStake);
            this.campusStakes.push(createdStake);
            this.selectedCampusStake = createdStake;

            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    }
  }

  /**
   * Deletes the selected stake.
   */
  delete() {
    if (!this.isEditing() || this.deleting) {
      return;
    }

    const stakeToDelete = this.campusStake;

    const message = `Are you sure you want to delete ${stakeToDelete.name} information and its associated leadership?`;
    const confirmDialog = this.matDialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: 'Please Confirm', message: message }
    });

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.turningOffFlags();

        this.deleting = true;
        this.churchUnitsDataService
          .deleteCampusStake(stakeToDelete.campusStakeId)
          .pipe(
            finalize(() => (this.deleting = false))
          )
          .subscribe(
            () => {
              const index = this.campusStakes.findIndex(s => s.campusStakeId === stakeToDelete.campusStakeId);
              this.campusStakes.splice(index, 1);

              this.deleted = true;
              this.clearSelectedStake();
            },
            error => {
              this.showError(error);
            }
          );
      }
    });
  }

  /**
   * Gets rid of the stake the user was editing.
   */
  cancel() {
    this.clearSelectedStake();
  }

  /**
   * Handles the things that need done when we've loaded a stake's information or after an update/create.
   */
  private setStakeDetails(stakeDetails: CampusStakeDetailsForUpdate) {
    this.campusStake = stakeDetails;

    if (this.campusStake.campusStakeId === 0) {
      this.actionTitle = 'Add a new Stake';
    } else {
      this.actionTitle = `Update Information: ${this.campusStake.name}`;
    }

    this.campusStakeForm.reset();
    this.campusStakeForm.patchValue(this.campusStake);
  }

  /**
   * Shows an error dialog to the user.
   */
  private showError(message: string) {
    this.matDialog.open(MessageDialog, {
      data: { message: message, isError: true }
    });
  }

  /**
   * Clears the selected stake from the dropdown as well as the form.
   */
  private clearSelectedStake() {
    this.campusStake = null;
    this.selectedCampusStake = null;
  }

  /**
   * Loads the dropdown to select stakes
   */
  private loadData() {
    if (!this.loading) {
      this.loading = true;
      this.churchUnitsDataService
        .getCampusStakes()
        .pipe(
          finalize(() => (this.loading = false))
        )
        .subscribe(
          data => {
            this.campusStakes = data;
          },
          error => {
            this.loadingError = true;
          }
        );
    }
  }
}
